<script>
import Spinner from "@/components/ui/Spinner";
import IndicationCard from "../appointment/IndicationCard.vue";
import FormRenderer from "@/components/ui/FormRenderer.vue";
import Indications from "@/components/appointment/indications/Indications.vue";
import { INDICATIONS_TYPES } from "@/services/constants";
import indicationService from "@/services/indication";
import formService from "@/services/form";
import { startCase, toLower, capitalize, cloneDeep } from "lodash";
import { mapGetters } from "vuex";
import { landingUrl, shopUrl } from "@/services/config";
import programPatientService from "@/services/programPatient";
import parametrizationService from "@/services/parametrization";
import fileUploadConfigService from "@/services/fileupload-config";
import api from "@/services/api";
import LabVitalsTable from "@/components/patient/dashboard/LabVitalsTable.vue";
import StudyResultModal from "@/components/patient/StudyResultModal.vue";

export default {
  name: "PatientHistory",
  props: {
    patientId: { type: String, required: true },
    /** @description Vitales de laboratorio recibidos del padre para mostrar inline */
    labVitals: { type: Object, default: () => ({}) },
  },
  components: {
    Spinner,
    IndicationCard,
    Indications,
    FormRenderer,
    StudyResultModal,
    LabVitalsTable,
  },
  filters: {
    /**
     * @description Trunca una URL larga para mostrar solo el dominio + path corto.
     * @param {string} url
     * @returns {string}
     */
    truncateUrl(url) {
      try {
        const u = new URL(url);
        const path =
          u.pathname.length > 20
            ? u.pathname.substring(0, 20) + "..."
            : u.pathname;
        return u.hostname + path;
      } catch {
        return url;
      }
    },
  },
  data() {
    return {
      isLoading: false,
      appointments: [],
      programs: [],
      editingIndicationId: null,
      editingIndicationData: null,
      editingIndicationType: null,
      showIndicationTitles: [
        "medications",
        "forms",
        "imagesResults",
        "labResults",
      ],
      selectedForm: null,
      allowedEditTypes: [],
      editTimeLimitInMinutes: null,
      editingFormData: null,
      selectedFormForRenderer: null,
      isFormRendererVisible: false,
      forms: [],
      /** @description Accordion primario de atenciones previas abierto por defecto */
      openPrimaryAccordion: true,
      /** @description ID de la atención expandida actualmente */
      openAppointmentId: null,
      /** @description Estado de los accordions secundarios por tipo { 'evolution': true, ... } */
      openSecondaryAccordions: {},
      /** @description Si el panel inline de vitales de laboratorio está visible */
      showLabVitals: false,
      expandedIndications: {},
      /** @description Estado del modal de carga de resultado */
      studyResultModal: {
        visible: false,
        type: "lab",
        sourceIndication: null,
      },
    };
  },
  computed: {
    ...mapGetters(["isPatient", "isAdmissionist", "isAdmin", "isDoctor"]),
    /**
     * @description Tipos de indicación ocultos para pacientes viendo su propia historia
     * @returns {string[]}
     */
    patientHiddenTypes() {
      return ["evolution", "forms", "practices", "programs"];
    },
    INDICATIONS_TYPES() {
      return INDICATIONS_TYPES;
    },
    iconMap() {
      const map = {};
      for (const item of INDICATIONS_TYPES) {
        if (item.type && item.micon) map[item.type] = item.micon;
      }
      return map;
    },
    groupedByType() {
      const typesMap = {};
      for (const { type } of INDICATIONS_TYPES) {
        typesMap[type] = [];
      }

      for (const appt of this.appointments) {
        for (const ind of appt.indications || []) {
          if (!typesMap[ind.type]) continue;
          let existingAppt = typesMap[ind.type].find((a) => a._id === appt._id);
          if (!existingAppt) {
            existingAppt = {
              _id: appt._id,
              startsAt: appt.startsAt,
              indications: [],
            };
            typesMap[ind.type].push(existingAppt);
          }
          existingAppt.indications.push(ind);
        }
      }

      return typesMap;
    },
    /**
     * @description Orden de prioridad para tipos de indicación
     * @returns {string[]}
     */
    indicationTypePriority() {
      return [
        "evolution",
        "medications",
        "lab",
        "labResults",
        "images",
        "imagesResults",
        "practices",
        "notes",
        "certificates",
        "forms",
        "program",
      ];
    },
    /**
     * @description Tipos de indicación ordenados por prioridad
     * @returns {Array}
     */
    orderedIndicationTypes() {
      const priority = this.indicationTypePriority;
      /** @description Solo mostrar tipos que están en la lista de prioridad */
      const filtered = INDICATIONS_TYPES.filter((t) =>
        priority.includes(t.type)
      );
      const sorted = [...filtered].sort((a, b) => {
        const idxA = priority.indexOf(a.type);
        const idxB = priority.indexOf(b.type);
        const posA =
          idxA >= 0 ? idxA : priority.length + INDICATIONS_TYPES.indexOf(a);
        const posB =
          idxB >= 0 ? idxB : priority.length + INDICATIONS_TYPES.indexOf(b);
        return posA - posB;
      });
      return sorted;
    },
    favoriteForms() {
      const favoriteAppointments = [];

      for (const appt of this.appointments) {
        const favoriteIndications = (appt.indications || []).filter(
          (ind) => ind.type === "forms" && ind.baseForm?.isFavorite === true
        );

        if (favoriteIndications.length > 0) {
          const favoriteAppt = {
            _id: appt._id,
            startsAt: appt.startsAt,
            indications: favoriteIndications,
            doctor: appt.doctor,
          };
          favoriteAppointments.push(favoriteAppt);
        }
      }

      return favoriteAppointments.sort(
        (a, b) => new Date(b.startsAt) - new Date(a.startsAt)
      );
    },

    /**
     * @description Lista mezclada y ordenada de órdenes y resultados de laboratorio
     * @returns {Array}
     */
    mergedLabItems() {
      const orders = (this.groupedByType["lab"] || []).flatMap((appt) =>
        appt.indications.map((ind) => ({
          ...ind,
          isResult: false,
          startsAt: appt.startsAt,
        }))
      );
      const results = (this.groupedByType["labResults"] || []).flatMap((appt) =>
        appt.indications.map((ind) => ({
          ...ind,
          isResult: true,
          startsAt: appt.startsAt,
        }))
      );
      return [...orders, ...results].sort(
        (a, b) =>
          new Date(b.createdAt || b.startsAt) -
          new Date(a.createdAt || a.startsAt)
      );
    },

    /**
     * @description Si hay datos de laboratorio para mostrar en el panel inline.
     * Se activa si hay resultados con análisis de IA O si el padre ya provee
     * vitales de laboratorio históricos (por ej. para rol paciente).
     * @returns {boolean}
     */
    hasAnalyzedLabResults() {
      const fromIndications = (this.groupedByType["labResults"] || []).some(
        (appt) => appt.indications.some((ind) => ind.aiAnalyzedAt)
      );
      const fromLabVitals = Object.keys(this.labVitals).length > 0;
      return fromIndications || fromLabVitals;
    },
  },
  async mounted() {
    await this.loadData();
    await this.loadAllowedEditTypes();
    await this.loadEditTimeLimit();
    await this.loadForms();
    this.$bus.$on("open-form-modal", this.handleOpenFormModal);
  },
  beforeDestroy() {
    this.$bus.$off("open-form-modal", this.handleOpenFormModal);
  },
  methods: {
    capitalize,

    /**
     * @description Ordena indicaciones por prioridad de tipo
     * @param {Array} indications - Lista de indicaciones
     * @returns {Array} Indicaciones ordenadas
     */
    sortedIndicationsByType(indications) {
      const priority = this.indicationTypePriority;
      return [...indications].sort((a, b) => {
        const idxA = priority.indexOf(a.type);
        const idxB = priority.indexOf(b.type);
        return (idxA >= 0 ? idxA : 99) - (idxB >= 0 ? idxB : 99);
      });
    },

    /**
     * @description Agrupa indicaciones por categoría con label e icono
     * @param {Array} indications - Lista de indicaciones
     * @returns {Array<{type: string, label: string, icon: string, indications: Array}>}
     */
    groupIndicationsByType(indications) {
      const sorted = this.sortedIndicationsByType(indications);
      const groups = [];
      let currentType = null;
      let currentGroup = null;

      for (const ind of sorted) {
        if (ind.type !== currentType) {
          const typeMeta = INDICATIONS_TYPES.find((t) => t.type === ind.type);
          currentGroup = {
            type: ind.type,
            label: typeMeta?.name || ind.type,
            icon: this.iconMap[ind.type] || "description",
            indications: [],
          };
          groups.push(currentGroup);
          currentType = ind.type;
        }
        currentGroup.indications.push(ind);
      }

      return groups;
    },

    /**
     * @description Genera resumen de mini-iconos por atención
     * @param {Object} appointment - Atención con indicaciones
     * @returns {Array<{type: string, count: number, icon: string}>}
     */
    getIndicationCountSummary(appointment) {
      const counts = {};
      for (const ind of appointment.indications || []) {
        if (ind.type === "imagesResults") continue;
        if (this.isPatient && this.patientHiddenTypes.includes(ind.type))
          continue;
        counts[ind.type] = (counts[ind.type] || 0) + 1;
      }
      const priority = this.indicationTypePriority;
      return Object.entries(counts)
        .sort(([a], [b]) => {
          const ia = priority.indexOf(a);
          const ib = priority.indexOf(b);
          return (ia >= 0 ? ia : 99) - (ib >= 0 ? ib : 99);
        })
        .map(([type, count]) => ({
          type,
          count,
          icon: this.iconMap[type] || "description",
        }));
    },

    /**
     * @description Obtiene nombre formateado del doctor de una atención
     * @param {Object} appointment - Atención
     * @returns {string}
     */
    getDoctorName(appointment) {
      if (!appointment.doctor) return "";
      const first = capitalize(appointment.doctor.firstName || "");
      const last = capitalize(appointment.doctor.lastName || "");
      return `${first} ${last}`.trim();
    },

    /**
     * @description Obtiene detalle/descripción secundaria de una indicación
     * @param {Object} ind - Indicación
     * @returns {string}
     */
    getIndicationDetail(ind) {
      switch (ind.type) {
        case "medications": {
          const parts = [];
          if (ind.dose) parts.push(ind.dose);
          if (ind.frequency) parts.push(ind.frequency);
          if (ind.duration) parts.push(ind.duration);
          return parts.join(" ") || ind.observations || "";
        }
        case "evolution": {
          const parts = [];
          if (ind.diagnosis?.description || ind.diagnosis?.name) {
            parts.push(ind.diagnosis.description || ind.diagnosis.name);
          }
          // Solo mostrar description en detail si no es lo mismo que el title
          // getIndicationTitle usa: ind.consultation || ind.description
          // Si no hay consultation, description ya aparece como title — no duplicar
          if (
            ind.description &&
            ind.consultation &&
            ind.description !== ind.consultation
          ) {
            parts.push(ind.description);
          }
          return parts.join(" · ");
        }
        case "notes":
          return ind.description || "";
        case "images":
          return ind.observations || "";
        case "lab": {
          // Mostrar las determinaciones como lista si existen
          if (ind.labs && ind.labs.length) {
            const names = ind.labs
              .map((l) => l.description || l.code)
              .filter(Boolean)
              .slice(0, 5)
              .join(", ");
            return (
              names +
              (ind.labs.length > 5 ? ` +${ind.labs.length - 5} más` : "")
            );
          }
          return ind.observations || "";
        }
        case "forms":
          return "";
        case "program":
          return ind.description || "";
        case "certificates":
          return ind.description || "";
        case "labResults":
        case "imagesResults":
          return ind.observations || "";
        default:
          return ind.description || ind.observations || "";
      }
    },

    async loadAllowedEditTypes() {
      try {
        const parametrization =
          await parametrizationService.findByParameterName(
            "EDIT_TYPES_INDICATIONS_AVAILABLE"
          );
        if (
          parametrization?.parameterValue &&
          parametrization.parameterType === "array" &&
          parametrization.parameterValue.length > 0
        ) {
          this.allowedEditTypes = parametrization.parameterValue;
        }
      } catch (error) {
        console.warn(
          "No se pudo obtener EDIT_TYPES_INDICATIONS_AVAILABLE desde parametrizations:",
          error
        );
      }
    },
    async loadEditTimeLimit() {
      try {
        const parametrization =
          await parametrizationService.findByParameterName(
            "EDIT_TIME_LIMIT_IN_M"
          );
        if (
          parametrization?.parameterValue &&
          parametrization.parameterType === "number" &&
          parametrization.parameterValue > 0
        ) {
          this.editTimeLimitInMinutes = parametrization.parameterValue;
        }
      } catch (error) {
        console.warn(
          "No se pudo obtener EDIT_TIME_LIMIT_IN_M desde parametrizations:",
          error
        );
      }
    },
    formatTime(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d)
        ? ""
        : d.toLocaleTimeString("es-AR", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
    },
    getIndicationTitle(indication) {
      switch (indication.type) {
        case "medications":
          return (
            indication.medication?.name ||
            indication.publicMedication?.name ||
            "Sin nombre"
          );
        case "images":
          return "Imagen médica";
        case "lab": {
          return startCase(toLower(indication.description)) || "Laboratorio";
        }
        case "practices":
          return indication.description || "Práctica médica";
        case "imagesResults":
          return indication.description || "Resultados de imágenes";
        case "labResults":
          return indication.description || "Resultados de laboratorio";
        case "evolution":
          return indication.consultation || indication.description || "";
        case "notes":
          return indication.description || "Nota médica";
        case "certificates":
          return indication.title || "Certificado";
        case "forms":
          return indication.name || "Formulario sin nombre";
        case "program":
          return indication.name || "Programa sin nombre";
        default:
          return "Indicación";
      }
    },
    shouldShowIndicationTitle(type) {
      return this.showIndicationTitles.includes(type);
    },
    shouldShowIcons(type) {
      return type === "imagesResults" || type === "labResults";
    },
    getIconConfigs(type) {
      const configs = {
        imagesResults: [
          {
            name: "description",
            tooltip: "Ver reportes",
            action: "viewReports",
          },
          {
            name: "radiology",
            tooltip: "Ver imágenes",
            action: "viewImages",
          },
        ],
        labResults: [
          {
            name: "description",
            tooltip: "Ver resultados",
            action: "viewResults",
          },
        ],
      };
      return configs[type] || [];
    },
    // FG: Acá deberíamos implementar las acciones específicas para cada botón
    handleIconClick(action) {
      switch (action) {
        case "viewReports":
          break;
        case "viewImages":
          break;
        case "viewResults":
          break;
        default:
          console.log("Acción no implementada:", action);
      }
    },

    async onEditIndication(indication) {
      if (indication.type === "forms" && indication.isForm) {
        let originalForm = null;

        if (indication.form?._id) {
          originalForm = this.forms.find((f) => f._id === indication.form._id);
        }

        if (!originalForm && indication.formId) {
          originalForm = this.forms.find((f) => f._id === indication.formId);
        }

        if (!originalForm && indication.name) {
          originalForm = this.forms.find((f) => f.name === indication.name);
        }

        if (originalForm) {
          this.editingIndicationId = null;
          this.editingIndicationData = null;
          this.editingIndicationType = null;

          this.selectedFormForRenderer = cloneDeep(originalForm);

          const editingData = cloneDeep(indication);
          editingData.sections = editingData.sections || [];
          if (!editingData.fileSection) {
            editingData.fileSection = {
              enable: false,
              files: [],
            };
          }

          this.editingFormData = editingData;
          this.isFormRendererVisible = true;
        } else {
          this.$message.error("No se pudo encontrar el formulario template");
          console.error("No se encontró formulario template para:", indication);
        }
      } else {
        this.isFormRendererVisible = false;
        this.selectedFormForRenderer = null;
        this.editingFormData = null;

        this.editingIndicationId = indication._id;
        this.editingIndicationData = { ...indication };
        this.editingIndicationType = indication.type;
      }
    },

    onCancelEdit() {
      this.editingIndicationId = null;
      this.editingIndicationData = null;
      this.editingIndicationType = null;
      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
      this.editingFormData = null;
    },

    async onSaveIndication() {
      this.editingIndicationId = null;
      this.editingIndicationData = null;
      this.editingIndicationType = null;

      await this.loadData();
    },
    getIndicationTypesInAppointment(appointment) {
      const types = new Set();
      for (const ind of appointment.indications || []) {
        types.add(ind.type);
      }
      return types;
    },
    editingIndicationForm() {
      if (!this.editingIndicationType) return null;
      const indicationMeta = INDICATIONS_TYPES.find(
        (t) => t.type === this.editingIndicationType
      );
      return (
        indicationMeta || {
          type: this.editingIndicationType,
          name: "Indicación",
        }
      );
    },
    getAdditionalIndicationTitles(appt) {
      if (!appt.indications?.length) return "";
      const additionalIndications = appt.indications.slice(1);
      return additionalIndications
        .map((ind) => this.getIndicationTitle(ind))
        .join(", ");
    },
    getIndicationTypeTooltip(appointment, typeInd) {
      const count =
        appointment.indications?.filter((ind) => ind.type === typeInd.type)
          .length || 0;
      return count > 0 ? `${typeInd.name} (${count})` : typeInd.name;
    },
    isTypeAllowed(indication) {
      if (this.isAdmissionist || this.isPatient) {
        return false;
      }
      return this.allowedEditTypes.includes(indication.type);
    },
    canEditIndication(indication) {
      if (!this.isTypeAllowed(indication)) {
        return false;
      }

      if (
        indication.type === "forms" &&
        indication.baseForm?.isFavorite === true
      ) {
        return true;
      }

      if (this.editTimeLimitInMinutes === null) {
        return true;
      }

      if (!indication.createdAt) {
        return false;
      }

      const createdAt = new Date(indication.createdAt);
      const now = new Date();
      const diffInMinutes = (now - createdAt) / (1000 * 60);

      return diffInMinutes <= this.editTimeLimitInMinutes;
    },
    /**
     * @description Carga indicaciones, formularios y programas del paciente.
     *              Para pacientes logueados, suprime toasts de error de permisos.
     */
    async loadData() {
      this.isLoading = true;
      try {
        const silentOpts = this.isPatient
          ? { getErrorMessage: () => null }
          : {};
        const safeCall = (promise) =>
          this.isPatient ? promise.catch(() => []) : promise;

        const [indications, forms, programPatients] = await Promise.all([
          safeCall(
            indicationService.get(this.patientId, undefined, silentOpts)
          ),
          // Siempre silenciar y tratar 404 como lista vacía — ocurre cuando
          // el paciente no tiene forms o el ID no es de tipo patient
          formService
            .getPatientFormsByPatient(this.patientId, {
              getErrorMessage: () => null,
            })
            .catch(() => []),
          safeCall(
            programPatientService.getProgramPatientsByPatientId(
              this.patientId,
              silentOpts
            )
          ),
        ]);

        this.programs = programPatients || [];

        const appointmentsMap = {};
        for (const ind of indications) {
          const apptId = ind.appointment || `no-appointment-${ind._id}`;
          if (!appointmentsMap[apptId]) {
            appointmentsMap[apptId] = {
              _id: apptId,
              startsAt: ind.createdAt,
              indications: [],
              doctor: ind.doctor,
            };
          }
          appointmentsMap[apptId].indications.push(ind);

          if (
            ind.appointment &&
            ind.createdAt > appointmentsMap[apptId].startsAt
          ) {
            appointmentsMap[apptId].startsAt = ind.createdAt;
          }
        }

        for (const form of forms || []) {
          const apptId = form.appointment || `no-appointment-${form._id}`;
          if (!appointmentsMap[apptId]) {
            appointmentsMap[apptId] = {
              _id: apptId,
              startsAt: form.answeredAt || form.createdAt,
              indications: [],
              doctor: form.doctor,
            };
          }

          const formIndication = {
            _id: form._id,
            type: "forms",
            name: form.name || form.form?.name || "Formulario sin nombre",
            sections: form.sections,
            fileSection: form.fileSection,
            createdAt: form.answeredAt || form.createdAt,
            isForm: true,
            form: form.form || { _id: form.form?._id || form.form },
            formId: form.form?._id || form.form,
            baseForm: form.baseForm,
          };

          appointmentsMap[apptId].indications.push(formIndication);

          if (
            form.appointment &&
            (form.answeredAt || form.createdAt) >
              appointmentsMap[apptId].startsAt
          ) {
            appointmentsMap[apptId].startsAt =
              form.answeredAt || form.createdAt;
          }
        }

        for (const programPatient of this.programs || []) {
          const apptId =
            programPatient.appointment || `program-${programPatient._id}`;
          if (!appointmentsMap[apptId]) {
            appointmentsMap[apptId] = {
              _id: apptId,
              startsAt: programPatient.createdAt,
              indications: [],
              doctor: programPatient.doctor || null,
              isStandaloneProgramAppointment: !programPatient.appointment,
            };
          }

          const programIndication = {
            _id: programPatient._id,
            type: "program",
            name: programPatient.program.name,
            description: programPatient.program.description,
            status: programPatient.status,
            initialData: programPatient.initialData,
            createdAt: programPatient.createdAt,
            isProgram: true,
          };

          appointmentsMap[apptId].indications.push(programIndication);

          if (
            programPatient.appointment &&
            programPatient.createdAt > appointmentsMap[apptId].startsAt
          ) {
            appointmentsMap[apptId].startsAt = programPatient.createdAt;
          }
        }

        this.appointments = Object.values(appointmentsMap).sort(
          (a, b) => new Date(b.startsAt) - new Date(a.startsAt)
        );
      } catch (error) {
        console.error("Error al cargar datos del paciente:", error);
      } finally {
        this.isLoading = false;
      }
    },
    getActionButtons(type, appointment) {
      const buttonConfigs = {
        medications: [
          { key: "buy", text: "Comprar", class: "button-blue-new" },
          { key: "download", text: "PDF", class: "button-green-new" },
        ],
        images: [
          {
            key: "appointment",
            text: "Gestionar cita",
            class: "button-blue-new",
          },
          { key: "download", text: "PDF", class: "button-green-new" },
        ],
        lab: [
          { key: "where", text: "Donde", class: "button-green-new" },
          { key: "download", text: "PDF", class: "button-green-new" },
        ],
      };

      const buttons = buttonConfigs[type] || [];

      if (type === "medications" && appointment) {
        const buyButton = buttons.find((button) => button.key === "buy");
        if (buyButton) {
          buyButton.disabled = this.isIndicationExpired({
            createdAt: appointment.startsAt,
          });
        }
      }

      return buttons;
    },

    /**
     * @description Verifica si una indicación está vencida usando expiresAt o fallback a 1 mes
     * @param {Object} indication - Indicación con expiresAt y/o createdAt
     * @returns {boolean}
     */
    isIndicationExpired(indication) {
      if (!indication) return false;
      if (indication.expiresAt) {
        return new Date(indication.expiresAt) < new Date();
      }
      if (!indication.createdAt) return false;
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      return new Date(indication.createdAt) < oneMonthAgo;
    },
    handleActionButton(buttonKey, button) {
      if (button && button.disabled) {
        return;
      }

      switch (buttonKey) {
        case "appointment":
          window.open(landingUrl, "_blank");
          break;
        case "buy":
          window.open(shopUrl, "_blank");
          break;
        // FG: Acá deberíamos implementar las otras acciones cuando sepamos
        default:
          console.error("Acción no implementada / errónea:", buttonKey);
      }
    },

    /**
     * @description Navega al flujo de compra de medicamento para una indicación
     * @param {Object} indication - Indicación de medicamento
     */
    goToPurchase(indication) {
      if (!indication || !indication._id) {
        this.$message.error("No se pudo identificar la receta");
        return;
      }
      this.$router
        .push({
          name: "prescription-purchase",
          params: { indicationId: indication._id },
        })
        .catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            console.error("Error de navegación:", err);
            this.$message.error("No se pudo abrir la compra de medicamento");
          }
        });
    },

    /**
     * @description Abre el modal para cargar un resultado de lab/imagen.
     * @param {Object} indication - Orden origen
     * @param {string} type - 'lab' | 'images' | 'labResults' | 'imagesResults'
     */
    /**
     * @description Abre la URL de compra de una prescripción en nueva pestaña
     * @param {string} url - URL de compra
     */
    openPurchaseUrl(url) {
      window.open(url, "_blank");
    },

    openUploadResult(indication, type) {
      const baseType =
        type === "labResults"
          ? "lab"
          : type === "imagesResults"
          ? "images"
          : type;
      this.studyResultModal = {
        visible: true,
        type: baseType,
        sourceIndication: indication,
      };
    },

    /**
     * @description Abre el archivo adjunto de una indicación via signed URL.
     * @param {Object} indication - Indicación con files[]
     */
    async openIndicationFile(indication) {
      try {
        const pdfFile = (indication.files || []).find(
          (f) => f.mimeType === "application/pdf" || f.key
        );
        if (!pdfFile) {
          this.$message.warning("No hay archivo adjunto");
          return;
        }
        const res = await api.get(
          `/indications/${indication._id}/files/${encodeURIComponent(
            pdfFile.key
          )}`
        );
        window.open(res.data.url, "_blank");
      } catch (err) {
        console.error("Error abriendo archivo:", err);
        this.$message.error("Error al abrir el archivo");
      }
    },

    /**
     * @description Descarga el PDF de una orden de lab/imagen.
     * @param {Object} indication - Indicación tipo lab/images
     */
    downloadIndicationPdf(indication) {
      // TODO: implementar descarga PDF de orden
      console.log("downloadIndicationPdf", indication._id);
    },

    /**
     * @description Llama al endpoint de análisis IA para extraer indicadores
     * del PDF adjunto y crear VitalTasks del paciente.
     * @param {Object} indication - Indicación tipo labResults con PDF adjunto
     */
    async openLabAnalysis(indication) {
      try {
        this.$message.info("Analizando laboratorio con IA...");
        const res = await api.post("/ai/lab-analysis", {
          indicationId: indication._id,
          patientId: this.patientId,
          performedAt: indication.performedAt || indication.createdAt,
        });

        const { created, notFound } = res.data;

        if (created && created.length) {
          this.$message.success(
            `${created.length} indicador${
              created.length > 1 ? "es" : ""
            } cargado${created.length > 1 ? "s" : ""} en seguimiento`
          );
        } else {
          this.$message.warning(
            "No se encontraron indicadores conocidos en el PDF"
          );
        }

        if (notFound && notFound.length) {
          console.info("Indicadores no encontrados en PDF:", notFound);
        }
      } catch (err) {
        console.error("Error en análisis de laboratorio:", err);
        this.$message.error(
          err.response?.data?.message || "Error al analizar el laboratorio"
        );
      }
    },

    /**
     * @description Togglea el panel inline de vitales de laboratorio.
     * Ya no navega a Seguimiento — los datos se muestran en la misma pantalla.
     */
    goToVitals() {
      this.showLabVitals = !this.showLabVitals;
    },

    /**
     * @description Handler cuando se guarda un resultado desde el modal.
     * Recarga los datos para reflejar el nuevo resultado.
     */
    async onStudyResultSaved() {
      await this.loadData();
    },

    /**
     * @description Determina si un tipo de indicación tiene acciones inline por fila
     * @param {string} type - Tipo de indicación
     * @returns {boolean}
     */
    hasRowActions(type) {
      return ["medications", "images", "lab"].includes(type);
    },

    async loadForms() {
      try {
        const opts = this.isPatient ? { getErrorMessage: () => null } : {};
        this.forms = await formService.getForms(opts);
      } catch (error) {
        console.error("Error al cargar formularios:", error);
        this.forms = [];
      }
    },

    async onFormRendererUpdated(updatedForm) {
      try {
        const formId = updatedForm._id;

        const immutableFields = [
          "_id",
          "organization",
          "patient",
          "episode",
          "entity",
          "createdBy",
          "createdAt",
          "appointment",
        ];

        const payload = {};
        Object.keys(updatedForm).forEach((key) => {
          if (!immutableFields.includes(key)) {
            payload[key] = updatedForm[key];
          }
        });

        if (updatedForm.hasFiles && updatedForm.dropzoneFiles?.length > 0) {
          await this.uploadFormFiles(formId, updatedForm.dropzoneFiles);
        }

        await formService.updatePatientForm(formId, payload);

        this.$message.success("Formulario actualizado correctamente");

        this.isFormRendererVisible = false;
        this.selectedFormForRenderer = null;
        this.editingFormData = null;

        await this.loadData();
      } catch (error) {
        console.error("Error al actualizar formulario:", error);
        this.$message.error(error.response?.data?.error || error.message);
      }
    },

    async uploadFormFiles(formId, files) {
      try {
        const formData = new FormData();

        files.forEach((file) => {
          const nativeFile = file.file || file;
          if (nativeFile instanceof File || nativeFile instanceof Blob) {
            formData.append("files", nativeFile, nativeFile.name);
          }
        });

        const uploadUrl = fileUploadConfigService.getFormFilesEndpoint(formId);
        await api.put(uploadUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$message.success("Archivos adjuntos subidos exitosamente");
      } catch (error) {
        console.error("Error al subir archivos del formulario:", error);
        this.$message.error(
          `Error al subir archivos: ${
            error.response?.data?.error || error.message
          }`
        );
      }
    },

    onFormRendererCancel() {
      this.isFormRendererVisible = false;
      this.selectedFormForRenderer = null;
      this.editingFormData = null;
    },

    currentPatient() {
      return {
        _id: this.patientId,
      };
    },
  },
};
</script>

<template lang="pug">
.ph-container
  spinner(v-if="isLoading")

  template(v-else-if="appointments.length")
    //- =============================================
    //- ATENCIONES PREVIAS — primary accordion, navy
    //- =============================================
    .ph-acc-primary(:class="{ open: openPrimaryAccordion }")
      .ph-acc-primary-trigger(@click="openPrimaryAccordion = !openPrimaryAccordion")
        micon.ph-acc-primary-icon(name="stethoscope" color="white")
        span.ph-acc-primary-label Atenciones previas
        span.ph-acc-primary-count {{ appointments.length }} {{ appointments.length === 1 ? 'atención' : 'atenciones' }}
        micon.ph-acc-primary-chevron(name="expand_more" color="white")

      .ph-acc-primary-body(v-show="openPrimaryAccordion")
        template(v-for="appointment in appointments")
          .ph-appt(
            v-if="appointment.indications.length && !appointment.isMock && !appointment.isStandaloneProgramAppointment"
            :key="appointment._id"
            :class="{ open: openAppointmentId === appointment._id }"
          )
            .ph-appt-header(@click="openAppointmentId = openAppointmentId === appointment._id ? null : appointment._id")
              span.ph-appt-date {{ appointment.startsAt | formatDate }}
              .ph-appt-summary
                span.ph-appt-summary-item(v-for="s in getIndicationCountSummary(appointment)" :key="s.type")
                  micon(:name="s.icon" width="15" height="15")
                  | {{ s.count }}
              span.ph-appt-doctor(v-if="appointment.doctor") {{ getDoctorName(appointment) }}
              span.ph-appt-time {{ formatTime(appointment.startsAt) }}
              micon.ph-appt-chevron(name="expand_more")

            .ph-ind-list(v-show="openAppointmentId === appointment._id")
              .ph-ind-group(
                v-for="group in groupIndicationsByType(appointment.indications)"
                v-if="!isPatient || !patientHiddenTypes.includes(group.type)"
                :key="group.type"
              )
                .ph-ind-group-label
                  micon(:name="group.icon" width="15" height="15")
                  | {{ group.label }}

                template(v-for="ind in group.indications")
                  //- FormRenderer en modo edición
                  FormRenderer(
                    v-if="isFormRendererVisible && editingFormData && editingFormData._id === ind._id"
                    :key="`form-edit-${ind._id}`"
                    :selectedForm="selectedFormForRenderer"
                    :patient="currentPatient()"
                    :isVisible="isFormRendererVisible"
                    :prefillData="editingFormData"
                    :isEditing="true"
                    @form-updated="onFormRendererUpdated"
                    @cancel-form="onFormRendererCancel"
                  )
                  //- Indications en modo edición
                  indications(
                    v-else-if="editingIndicationId === ind._id"
                    :key="`ind-edit-${ind._id}`"
                    :form="editingIndicationForm()"
                    :savedIndications="[]"
                    :appointment="null"
                    :existingIndication="editingIndicationData"
                    :mode="'edit'"
                    :typeName="INDICATIONS_TYPES.find(t => t.type === editingIndicationType)?.name || 'indicación'"
                    @saved="onSaveIndication"
                    @cancel="onCancelEdit"
                  )
                  //- Vista normal de la indicación
                  .ph-ind(v-else :key="`ind-${ind._id}`")
                    .ph-ind-dot
                    .ph-ind-body(@click="$set(expandedIndications, ind._id, !expandedIndications[ind._id])")
                      //- Evolución clínica: motivo, diagnóstico y descripción en filas separadas
                      template(v-if="ind.type === 'evolution'")
                        .ph-ind-name(
                          v-if="ind.consultation"
                          :class="{ 'ph-ind-truncated': !expandedIndications[ind._id] }"
                        )
                          strong Motivo de consulta:&nbsp;
                          | {{ ind.consultation }}
                        .ph-ind-detail(
                          v-if="ind.diagnosis && (ind.diagnosis.description || ind.diagnosis.name)"
                          :class="{ 'ph-ind-truncated': !expandedIndications[ind._id] }"
                        )
                          strong Diagnóstico:&nbsp;
                          | {{ ind.diagnosis.description || ind.diagnosis.name }}
                        .ph-ind-detail(
                          v-if="ind.description"
                          :class="{ 'ph-ind-truncated': !expandedIndications[ind._id] }"
                        ) {{ ind.description }}
                      //- Resto de tipos
                      template(v-else)
                        .ph-ind-name(
                          v-if="ind.type !== 'notes'"
                          :class="{ 'ph-ind-truncated': !expandedIndications[ind._id] }"
                        ) {{ getIndicationTitle(ind) }}
                        .ph-ind-detail(
                          v-if="getIndicationDetail(ind)"
                          :class="{ 'ph-ind-truncated': !expandedIndications[ind._id] }"
                        ) {{ getIndicationDetail(ind) }}
                        .ph-ind-detail(v-if="ind.type === 'notes' && !getIndicationDetail(ind)"
                          :class="{ 'ph-ind-truncated': !expandedIndications[ind._id] }"
                        ) {{ getIndicationTitle(ind) }}
                    button.ph-ind-edit(
                      v-if="canEditIndication(ind)"
                      @click.stop="onEditIndication(ind)"
                    ) Editar
                    template(v-if="ind.type === 'medications'")
                      button.ph-row-btn.ph-row-btn--buy(
                        :class="{ 'ph-row-btn--disabled': isIndicationExpired(ind) }"
                        :disabled="isIndicationExpired(ind)"
                        :title="isIndicationExpired(ind) ? 'La receta ya expiró y no puede usarse para comprar' : ''"
                        @click.stop="!isIndicationExpired(ind) && goToPurchase(ind)"
                      ) Comprar
                      button.ph-row-btn.ph-row-btn--pdf(
                        @click.stop="downloadIndicationPdf(ind)"
                      ) PDF
                    button.ph-ind-upload(
                      v-if="ind.type === 'lab' || ind.type === 'images'"
                      @click.stop="openUploadResult(ind, ind.type)"
                    ) + Resultado

    //- =============================================
    //- SECCIONES POR TIPO — secondary accordions
    //- =============================================
    .ph-acc-secondary-group
      template(v-for="typeInd in orderedIndicationTypes")

        //- ── Laboratorios unificado (solo renderizar cuando typeInd.type === 'lab') ──
        template(v-if="typeInd.type === 'lab' && (!isPatient || !patientHiddenTypes.includes('lab'))")
          .ph-acc(
            :key="'lab-unified'"
            :class="{ open: openSecondaryAccordions['lab'] }"
          )
            .ph-acc-trigger(@click="(groupedByType['lab'] && groupedByType['lab'].length) || (groupedByType['labResults'] && groupedByType['labResults'].length) ? $set(openSecondaryAccordions, 'lab', !openSecondaryAccordions['lab']) : null")
              micon.ph-acc-icon(:name="iconMap['lab']" width="18" height="18")
              span.ph-acc-label Laboratorios
              .ph-acc-header-actions(@click.stop)
                button.ph-acc-action-btn.ph-acc-action-btn--teal(
                  v-if="hasAnalyzedLabResults"
                  @click="goToVitals()"
                  :class="{ 'ph-acc-action-btn--active': showLabVitals }"
                )
                  | {{ showLabVitals ? 'Ocultar resultados' : 'Ver resultados' }}
                  micon(name="expand_more" width="14" height="14" :style="{ transform: showLabVitals ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }")
                button.ph-acc-action-btn.ph-acc-action-btn--primary(
                  @click="openUploadResult(null, 'lab')"
                ) + Cargar resultado
              span.ph-acc-count(v-if="(groupedByType['lab'] && groupedByType['lab'].length) || (groupedByType['labResults'] && groupedByType['labResults'].length)")
                | {{ (groupedByType['lab'] || []).reduce((s, a) => s + a.indications.length, 0) + (groupedByType['labResults'] || []).reduce((s, a) => s + a.indications.length, 0) }}
              span.ph-acc-count.ph-acc-count--empty(v-else) 0
              micon.ph-acc-chevron(name="expand_more" width="18" height="18")

            //- Panel inline de vitales de laboratorio
            .ph-lab-vitals-panel(v-if="showLabVitals")
              .ph-lab-vitals-panel__header
                span.ph-lab-vitals-panel__title
                  micon(name="monitoring" width="15" height="15")
                  | Indicadores de laboratorio — evolución
                button.ph-lab-vitals-panel__close(@click="showLabVitals = false")
                  micon(name="clear" width="14" height="14")
                  | Cerrar
              lab-vitals-table(:vitals="labVitals")

            .ph-acc-body(v-show="openSecondaryAccordions['lab']")
              .ph-lab-row(
                v-for="item in mergedLabItems"
                :key="item._id"
              )
                span.ph-lab-date {{ item.createdAt || item.startsAt | formatDate }}
                span.ph-lab-time {{ formatTime(item.createdAt || item.startsAt) }}
                span.ph-lab-badge(:class="item.isResult ? (item.aiAnalyzedAt ? 'ph-lab-badge--analyzed' : 'ph-lab-badge--result') : 'ph-lab-badge--order'")
                  | {{ item.isResult ? (item.aiAnalyzedAt ? 'Resultado analizado' : 'Resultado') : 'Orden' }}
                .ph-lab-desc(v-if="item.isResult")
                  span {{ item.description || 'Resultado de laboratorio' }}
                .ph-lab-chips(v-else-if="item.labs && item.labs.length")
                  span.ph-lab-chip(
                    v-for="lab in item.labs.slice(0, 4)"
                    :key="lab._id"
                  ) {{ lab.description || lab.code }}
                  span.ph-lab-chip.ph-lab-chip--more(v-if="item.labs.length > 4") +{{ item.labs.length - 4 }}
                .ph-lab-desc(v-else)
                  span.ph-lab-empty Sin determinaciones
                .ph-lab-actions
                  button.ph-row-btn(
                    v-if="item.isResult && item.files && item.files.length"
                    @click.stop="openIndicationFile(item)"
                  ) Ver PDF
                  button.ph-row-btn.ph-row-btn--ai(
                    v-if="item.isResult && item.files && item.files.find(f => f.mimeType === 'application/pdf') && !item.aiAnalyzedAt"
                    @click.stop="openLabAnalysis(item)"
                  ) Analizar con IA

              .ph-empty-message(v-if="!mergedLabItems.length") Sin registros

        //- ── Imágenes unificado (solo renderizar cuando typeInd.type === 'images') ──
        template(v-else-if="typeInd.type === 'images' && (!isPatient || !patientHiddenTypes.includes('images'))")
          .ph-acc(
            :key="'images-unified'"
            :class="{ open: openSecondaryAccordions['images'] }"
          )
            .ph-acc-trigger(@click="(groupedByType['images'] && groupedByType['images'].length) || (groupedByType['imagesResults'] && groupedByType['imagesResults'].length) ? $set(openSecondaryAccordions, 'images', !openSecondaryAccordions['images']) : null")
              micon.ph-acc-icon(:name="iconMap['images']" width="18" height="18")
              span.ph-acc-label Imágenes
              span.ph-acc-count(v-if="(groupedByType['images'] && groupedByType['images'].length) || (groupedByType['imagesResults'] && groupedByType['imagesResults'].length)")
                | {{ (groupedByType['images'] || []).reduce((s, a) => s + a.indications.length, 0) + (groupedByType['imagesResults'] || []).reduce((s, a) => s + a.indications.length, 0) }}
              span.ph-acc-count.ph-acc-count--empty(v-else) 0
              micon.ph-acc-chevron(name="expand_more" width="18" height="18")

            .ph-acc-body(v-show="openSecondaryAccordions['images']")
              template(v-if="groupedByType['images'] && groupedByType['images'].length")
                .ph-study-divider Órdenes
                template(v-for="appt in groupedByType['images']")
                  .ph-type-row.ph-type-row--study(
                    v-for="ind in appt.indications"
                    :key="ind._id"
                  )
                    .ph-study-main
                      .ph-study-top
                        span.ph-type-row-date {{ (ind.createdAt || appt.startsAt) | formatDate }}
                        span.ph-study-badge.ph-study-badge--order Orden
                        span.ph-type-row-time {{ formatTime(ind.createdAt || appt.startsAt) }}
                      .ph-study-actions
                        button.ph-row-btn(
                          v-if="ind.files && ind.files.length"
                          @click.stop="downloadIndicationPdf(ind)"
                        ) PDF
                        button.ph-row-btn.ph-row-btn--add(@click.stop="openUploadResult(ind, 'images')") + Cargar resultado
              template(v-if="groupedByType['imagesResults'] && groupedByType['imagesResults'].length")
                .ph-study-divider Resultados
                template(v-for="appt in groupedByType['imagesResults']")
                  .ph-type-row.ph-type-row--study.ph-type-row--result(
                    v-for="ind in appt.indications"
                    :key="ind._id"
                  )
                    .ph-study-main
                      .ph-study-top
                        span.ph-type-row-date {{ (ind.performedAt || ind.createdAt || appt.startsAt) | formatDate }}
                        span.ph-study-badge.ph-study-badge--result Resultado
                        span.ph-type-row-time {{ formatTime(ind.performedAt || ind.createdAt || appt.startsAt) }}
                      .ph-study-title {{ ind.description || 'Resultado de imagen' }}
                      template(v-if="ind.externalLinks && ind.externalLinks.length")
                        a.ph-study-link(
                          v-for="link in ind.externalLinks"
                          :key="link.url"
                          :href="link.url"
                          target="_blank"
                          rel="noopener"
                          @click.stop
                        )
                          span.ph-study-link-icon &#x1F517;
                          span.ph-study-link-label {{ link.label || 'Ver estudio' }}
                          span.ph-study-link-url {{ link.url | truncateUrl }}
                      .ph-study-actions
                        button.ph-row-btn(v-if="ind.files && ind.files.length" @click.stop="openIndicationFile(ind)") Ver PDF
                        button.ph-row-btn.ph-row-btn--add(@click.stop="openUploadResult(ind, 'imagesResults')") + Agregar
              .ph-empty-message(v-if="(!groupedByType['images'] || !groupedByType['images'].length) && (!groupedByType['imagesResults'] || !groupedByType['imagesResults'].length)") Sin registros

        //- ── Resto de tipos (saltear labResults e imagesResults, ya incluidos arriba) ──
        template(v-else-if="!['labResults','imagesResults'].includes(typeInd.type) && (!isPatient || !patientHiddenTypes.includes(typeInd.type))")
          .ph-acc(
            :key="typeInd.type"
            :class="{ open: openSecondaryAccordions[typeInd.type] }"
          )
            .ph-acc-trigger(@click="groupedByType[typeInd.type] && groupedByType[typeInd.type].length ? $set(openSecondaryAccordions, typeInd.type, !openSecondaryAccordions[typeInd.type]) : null")
              micon.ph-acc-icon(:name="iconMap[typeInd.type]" width="18" height="18")
              span.ph-acc-label {{ typeInd.name }}
              span.ph-acc-count(v-if="groupedByType[typeInd.type] && groupedByType[typeInd.type].length") {{ groupedByType[typeInd.type].reduce((sum, a) => sum + a.indications.length, 0) }}
              span.ph-acc-count.ph-acc-count--empty(v-else) 0
              micon.ph-acc-chevron(name="expand_more" width="18" height="18")

            .ph-acc-body(v-show="openSecondaryAccordions[typeInd.type]")
              template(v-if="groupedByType[typeInd.type] && groupedByType[typeInd.type].length")
                template(v-if="isPatient && hasRowActions(typeInd.type)")
                  template(v-for="appt in groupedByType[typeInd.type]")
                    .ph-type-row(
                      v-for="ind in appt.indications"
                      :key="ind._id"
                    )
                      span.ph-type-row-date {{ (ind.createdAt || appt.startsAt) | formatDate }}
                      span.ph-type-row-title {{ getIndicationTitle(ind) }}
                      span.ph-type-row-time {{ formatTime(ind.createdAt || appt.startsAt) }}
                      .ph-type-row-actions
                        button.ph-row-btn.ph-row-btn--buy(
                          v-if="typeInd.type === 'medications'"
                          :class="{ 'ph-row-btn--disabled': isIndicationExpired(ind) }"
                          :disabled="isIndicationExpired(ind)"
                          :title="isIndicationExpired(ind) ? 'La receta ya expiró y no puede usarse para comprar' : ''"
                          @click.stop="!isIndicationExpired(ind) && goToPurchase(ind)"
                        ) Comprar
                        button.ph-row-btn.ph-row-btn--pdf(
                          @click.stop="handleActionButton('download')"
                        ) PDF
                template(v-else)
                  .ph-type-row(
                    v-for="appt in groupedByType[typeInd.type]"
                    :key="appt._id"
                  )
                    span.ph-type-row-date {{ appt.startsAt | formatDate }}
                    span.ph-type-row-title {{ getIndicationTitle(appt.indications[0]) }}
                    span.ph-type-row-extra(v-if="appt.indications.length > 1") +{{ appt.indications.length - 1 }}
                    span.ph-type-row-time {{ formatTime(appt.startsAt) }}
              .ph-empty-message(v-else) Sin registros

  //- BUG-008: pacientes sin atenciones previas veían la pantalla en blanco
  //- (solo spinner que desaparecía). Mostramos un estado vacío explícito.
  template(v-else)
    .ph-empty-state
      micon.ph-empty-state-icon(name="event_available")
      p.ph-empty-state-text Aún no hay atenciones previas registradas.

  .ph-upload-actions(v-if="isPatient")
    button.ph-upload-btn(@click="openUploadResult(null, 'lab')")
      | + Cargar resultado de laboratorio
    button.ph-upload-btn(@click="openUploadResult(null, 'images')")
      | + Cargar resultado de imagen

  study-result-modal(
    v-if="studyResultModal.visible"
    v-model="studyResultModal.visible"
    :patientId="patientId"
    :type="studyResultModal.type"
    :sourceIndication="studyResultModal.sourceIndication"
    @saved="onStudyResultSaved"
  )
</template>

<style scoped>
/* =============================================
   VARIABLES
   ============================================= */
.ph-container {
  /* UC-REG-052 (fix definitivo): el min-width:0 anterior en .container-boxes
     .left no propagaba a través de los flex containers anidados (.ph-acc,
     .ph-appt, .ph-ind, etc.), entonces texto largo en .ph-ind-name de
     evolución clínica seguía empujando el ancho. Force overflow horizontal
     hidden + max-width:100% al wrapper top de PatientHistory para garantizar
     que NADA dentro se salga del card aunque la cadena de flex children
     tenga widths sin constraint. */
  max-width: 100%;
  overflow-x: hidden;
  --navy-700: #002d5e;
  --navy-600: #0a3d7a;
  --navy-400: #3b6ea5;
  --navy-100: #e1eaf6;
  --navy-50: #eef3fa;
  --teal-500: #0d9d9d;
  --teal-100: #e0f7f7;
  --gray-50: #f5f7fa;
  --gray-100: #e2e8f0;
  --gray-200: #cbd5e1;
  --gray-400: #94a3b8;
  --gray-500: #64748b;
  --gray-600: #475569;
  --gray-700: #334155;
}

/* =============================================
   SECTION TITLE
   ============================================= */
.ph-section-title {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: var(--navy-700);
  margin-bottom: 16px;
}

/* =============================================
   ATENCIONES PREVIAS — primary accordion
   ============================================= */
.ph-acc-primary {
  margin-bottom: 16px;
}
.ph-acc-primary-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--navy-700);
  border: 1.5px solid var(--navy-700);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.12s;
}
.ph-acc-primary-trigger:hover {
  background: var(--navy-600);
  border-color: var(--navy-600);
}
.ph-acc-primary.open > .ph-acc-primary-trigger {
  border-radius: 10px 10px 0 0;
}
.ph-acc-primary-icon {
  flex-shrink: 0;
  opacity: 0.7;
}
.ph-acc-primary-label {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: white;
}
.ph-acc-primary-count {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: white;
  background: rgba(255, 255, 255, 0.18);
  padding: 3px 12px;
  border-radius: 999px;
  margin-left: auto;
}
.ph-acc-primary-chevron {
  flex-shrink: 0;
  opacity: 0.5;
  transition: transform 0.15s;
}
.ph-acc-primary.open > .ph-acc-primary-trigger .ph-acc-primary-chevron {
  transform: rotate(180deg);
}
.ph-acc-primary-body {
  border: 1.5px solid var(--gray-200);
  border-top: none;
  border-radius: 0 0 10px 10px;
  background: white;
  overflow: hidden;
}

/* =============================================
   SECONDARY ACCORDIONS — type sections
   ============================================= */
.ph-acc-secondary-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ph-acc-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  background: white;
  border: 1.5px solid var(--gray-100);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.12s;
}
.ph-acc-trigger:hover {
  border-color: var(--gray-200);
  background: var(--gray-50);
}
.ph-acc.open > .ph-acc-trigger {
  border-radius: 8px 8px 0 0;
  border-color: var(--gray-200);
  background: var(--gray-50);
}
.ph-acc-icon {
  flex-shrink: 0;
}
.ph-acc-label {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--gray-600);
}
.ph-acc.open > .ph-acc-trigger .ph-acc-label {
  color: var(--navy-700);
}
.ph-acc-count {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: var(--navy-400);
  margin-left: auto;
}
.ph-acc-count--empty {
  color: var(--gray-400);
}
.ph-acc-chevron {
  transition: transform 0.15s;
  flex-shrink: 0;
}
.ph-acc.open > .ph-acc-trigger .ph-acc-chevron {
  transform: rotate(180deg);
}
.ph-acc-body {
  border: 1.5px solid var(--gray-100);
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: white;
  overflow: hidden;
}

/* =============================================
   APPOINTMENT ROWS (inside Atenciones previas)
   ============================================= */
.ph-appt {
  border-bottom: 1px solid var(--gray-100);
}
.ph-appt:last-child {
  border-bottom: none;
}
.ph-appt.open {
  border-bottom: 2px solid var(--gray-200);
  margin-bottom: 2px;
}
.ph-appt-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  gap: 12px;
  cursor: pointer;
  transition: background 0.1s;
}
.ph-appt-header:hover {
  background: var(--gray-50);
}
.ph-appt.open > .ph-appt-header {
  background: var(--gray-50);
}

.ph-appt-date {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--navy-700);
  min-width: 80px;
  flex-shrink: 0;
  text-align: left;
}
.ph-appt-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.ph-appt-summary-item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12.5px;
  color: var(--navy-400);
}
.ph-appt-doctor {
  font-size: 12.5px;
  color: var(--gray-500);
  flex-shrink: 0;
  text-align: right;
}
.ph-appt-time {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--gray-400);
  flex-shrink: 0;
  min-width: 36px;
  text-align: right;
}
.ph-appt-chevron {
  transition: transform 0.12s;
  flex-shrink: 0;
}
.ph-appt.open > .ph-appt-header .ph-appt-chevron {
  transform: rotate(180deg);
}

/* =============================================
   INDICATION ITEMS — grouped by category
   ============================================= */
.ph-ind-list {
  padding: 8px 14px 12px 40px;
  background: #f7f8fa;
}

/* Category group */
.ph-ind-group {
  padding: 8px 0 4px;
  border-bottom: 1px solid var(--gray-100);
}
.ph-ind-group:last-child {
  border-bottom: none;
}

/* Category label */
.ph-ind-group-label {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--navy-400);
  padding: 0 10px 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.ph-ind-group-label micon {
  flex-shrink: 0;
}

/* Individual indication */
.ph-ind {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 5px 10px 5px 28px;
  border-radius: 6px;
  transition: background 0.1s;
  margin-bottom: 1px;
}
.ph-ind:hover {
  background: rgba(0, 0, 0, 0.02);
}
.ph-ind-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  margin-top: 7px;
  flex-shrink: 0;
  background: var(--navy-400);
  opacity: 0.5;
}
.ph-ind-body {
  flex: 1;
  min-width: 0;
}
.ph-ind-name {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--gray-700);
  line-height: 1.3;
  /* Respeta saltos de línea en descripciones multilínea (ej. resultados
     de lab vía ORU^R01: "Glucemia: 95\nHemoglobina: 14.2..."). No afecta
     strings sin \n. Ver UC-REG-049. */
  white-space: pre-line;
  /* Fuerza wrap en cualquier carácter si una palabra excede el ancho del
     contenedor (caso típico: descripción larga de evolución clínica sin
     espacios o con palabras técnicas largas). Ver UC-REG-051. */
  overflow-wrap: anywhere;
}
.ph-ind-detail {
  font-size: 12.5px;
  color: var(--gray-500);
  line-height: 1.4;
  margin-top: 1px;
  /* Idem para detalles multilínea. */
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.ph-ind-edit {
  padding: 3px 8px;
  border-radius: 5px;
  border: 1px solid var(--gray-100);
  background: white;
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--gray-500);
  cursor: pointer;
  transition: all 0.1s;
  flex-shrink: 0;
  margin-top: 2px;
}
.ph-ind-edit:hover {
  border-color: var(--teal-500);
  color: var(--teal-500);
}
.ph-ind-upload {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 5px;
  border: 1px solid #c8d6e8;
  background: var(--navy-100);
  color: var(--navy-600);
  cursor: pointer;
  transition: all 0.12s;
  white-space: nowrap;
  margin-left: 4px;
}
.ph-ind-upload:hover {
  background: var(--navy-50);
  border-color: var(--navy-400);
}

/* =============================================
   TYPE-GROUPED ROWS (inside secondary accordions)
   ============================================= */
.ph-type-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--gray-100);
  cursor: pointer;
  transition: background 0.1s;
}
.ph-type-row:last-child {
  border-bottom: none;
}
.ph-type-row:hover {
  background: var(--gray-50);
}
.ph-type-row-date {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: var(--navy-700);
  min-width: 80px;
  flex-shrink: 0;
  text-align: left;
}
.ph-type-row-title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: var(--gray-600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ph-type-row-extra {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: var(--navy-400);
  background: var(--navy-100);
  padding: 1px 6px;
  border-radius: 999px;
  border: 1px solid #c8d6e8;
  flex-shrink: 0;
}
.ph-type-row-time {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--gray-400);
  flex-shrink: 0;
  text-align: right;
}

/* =============================================
   PER-ROW ACTION BUTTONS (patient view)
   ============================================= */
.ph-type-row-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.ph-row-btn {
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 5px;
  border: 1px solid var(--gray-200);
  background: white;
  cursor: pointer;
  transition: all 0.12s;
  white-space: nowrap;
}
.ph-row-btn:hover {
  border-color: var(--teal-500);
  color: var(--teal-500);
}
.ph-row-btn--buy {
  background: var(--teal-500);
  color: white;
  border-color: var(--teal-500);
}
.ph-row-btn--buy:hover {
  background: #0b8a8a;
  border-color: #0b8a8a;
  color: white;
}
.ph-row-btn--buy.ph-row-btn--disabled,
.ph-row-btn--buy.ph-row-btn--disabled:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
  color: #94a3b8;
  cursor: not-allowed;
}
.ph-row-btn--pdf {
  color: var(--gray-600);
}

.ph-empty-message {
  padding: 12px 14px;
  font-size: 13px;
  color: var(--gray-400);
  font-style: italic;
}

/* BUG-008: estado vacío de Historia Clínica */
.ph-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  text-align: center;
  background: var(--gray-50, #f8fafc);
  border: 1px dashed var(--gray-200, #e2e8f0);
  border-radius: 12px;
  margin: 12px 0;
}
.ph-empty-state-icon {
  color: var(--gray-400);
  margin-bottom: 8px;
}
.ph-empty-state-text {
  margin: 0;
  font-size: 14px;
  color: var(--gray-600);
}

/* =============================================
   RESPONSIVE — mobile
   ============================================= */
@media (max-width: 768px) {
  .ph-appt-header {
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px 12px;
  }
  .ph-appt-date {
    font-size: 11.5px;
    min-width: 70px;
  }
  .ph-appt-doctor {
    display: none;
  }
  .ph-appt-summary {
    flex: 1 1 auto;
    min-width: 0;
  }

  .ph-ind-list {
    padding: 8px 10px 12px 16px;
  }
  .ph-ind {
    padding: 5px 6px 5px 12px;
  }
  .ph-ind-name {
    font-size: 12.5px;
  }
  .ph-ind-detail {
    font-size: 11.5px;
  }

  /* Type rows: stack as mini-cards */
  .ph-type-row {
    flex-wrap: wrap;
    gap: 4px 8px;
    padding: 10px 12px;
  }
  .ph-type-row-date {
    font-size: 11px;
    min-width: 65px;
  }
  .ph-type-row-title {
    font-size: 12px;
    flex: 1 1 calc(100% - 80px);
    white-space: normal;
    overflow: visible;
  }
  .ph-type-row-time {
    font-size: 10px;
  }
  .ph-type-row-actions {
    flex: 1 1 100%;
    justify-content: flex-start;
    gap: 8px;
    margin-top: 4px;
  }
  .ph-row-btn {
    font-size: 11px;
    padding: 5px 14px;
  }

  .ph-acc-trigger {
    padding: 10px 12px;
    gap: 8px;
  }
  .ph-acc-label {
    font-size: 12px;
  }
}

/* =============================================
   STUDY ROWS — lab, images, labResults, imagesResults
   ============================================= */
.ph-type-row--study {
  align-items: flex-start;
  cursor: default;
}
.ph-type-row--result {
  background: #f4fbfa;
}
.ph-type-row--result:hover {
  background: #ecf8f6;
}
.ph-study-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ph-study-top {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ph-study-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-700);
}
.ph-study-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  flex-shrink: 0;
}
.ph-study-badge--order {
  background: var(--navy-100);
  color: var(--navy-600);
  border: 1px solid #c8d6e8;
}
.ph-study-badge--result {
  background: var(--teal-100);
  color: var(--teal-500);
  border: 1px solid #9fe1cb;
}
.ph-study-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.ph-study-chip {
  font-size: 11px;
  padding: 2px 8px;
  background: var(--gray-50);
  border: 1px solid var(--gray-100);
  border-radius: 999px;
  color: var(--gray-600);
}
.ph-study-chip--more {
  background: var(--navy-100);
  color: var(--navy-400);
  border-color: #c8d6e8;
  font-weight: 700;
}
.ph-study-divider {
  font-size: 11px;
  font-weight: 700;
  color: var(--gray-400);
  padding: 5px 14px;
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-100);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.ph-study-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.ph-row-btn--add {
  background: var(--navy-100);
  color: var(--navy-600);
  border-color: #c8d6e8;
}
.ph-row-btn--add:hover {
  background: var(--navy-50);
  border-color: var(--navy-400);
  color: var(--navy-700);
}
.ph-row-btn--ai {
  background: var(--teal-100);
  color: var(--teal-500);
  border-color: #9fe1cb;
}
.ph-row-btn--ai:hover {
  background: #c8f0ee;
  border-color: var(--teal-500);
}
.ph-study-badge--analyzed {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  background: #e0f7f7;
  color: var(--teal-500);
  border: 1px solid #9fe1cb;
  flex-shrink: 0;
}
.ph-row-btn--data {
  background: var(--teal-100);
  color: var(--teal-500);
  border-color: #9fe1cb;
}
.ph-row-btn--data:hover {
  background: #c8f0ee;
  border-color: var(--teal-500);
}
.ph-study-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--navy-600);
  background: var(--navy-50);
  border: 1px solid #c8d6e8;
  border-radius: 6px;
  padding: 3px 10px;
  text-decoration: none;
  cursor: pointer;
  width: fit-content;
}
.ph-study-link:hover {
  background: var(--navy-100);
  border-color: var(--navy-400);
}
.ph-study-link-icon {
  font-size: 12px;
}
.ph-study-link-label {
  font-weight: 600;
  font-size: 12px;
}
.ph-study-link-url {
  font-size: 11px;
  color: var(--navy-400);
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ph-acc-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
}

.ph-acc-action-btn {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  font-family: "Plus Jakarta Sans", sans-serif;
}

.ph-acc-action-btn--primary {
  background: #002d5e;
  color: white;
  border: none;
}
.ph-acc-action-btn--primary:hover {
  background: #0a3d7a;
}

.ph-acc-action-btn--teal {
  background: #e0f7f7;
  color: #085041;
  border: 1px solid #9fe1cb;
}
.ph-acc-action-btn--teal:hover {
  background: #c0eeea;
}
.ph-acc-action-btn--teal.ph-acc-action-btn--active {
  background: #0f766e;
  color: #fff;
  border-color: #0f766e;
}
.ph-acc-action-btn--teal.ph-acc-action-btn--active:hover {
  background: #0d6560;
}
.ph-acc-action-btn--teal .micon {
  fill: #085041;
  vertical-align: middle;
  margin-left: 2px;
}
.ph-acc-action-btn--teal.ph-acc-action-btn--active .micon {
  fill: #fff;
}

/* ── Panel inline de vitales de laboratorio ── */
.ph-lab-vitals-panel {
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.ph-lab-vitals-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}
.ph-lab-vitals-panel__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #1e3a5f;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ph-lab-vitals-panel__title .micon {
  fill: #0f766e;
}
.ph-lab-vitals-panel__close {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
  font-family: inherit;
}
.ph-lab-vitals-panel__close .micon {
  fill: #94a3b8;
}
.ph-lab-vitals-panel__close:hover {
  background: #f1f5f9;
}

.ph-lab-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-bottom: 0.5px solid var(--gray-100);
}
.ph-lab-row:last-child {
  border-bottom: none;
}

.ph-lab-date {
  font-size: 12px;
  font-weight: 500;
  color: var(--navy-700);
  min-width: 78px;
  flex-shrink: 0;
}

.ph-lab-time {
  font-size: 11px;
  color: var(--gray-400);
  min-width: 38px;
  flex-shrink: 0;
}

.ph-lab-badge {
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 10px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.ph-lab-badge--order {
  background: var(--navy-100);
  color: var(--navy-600);
}
.ph-lab-badge--result {
  background: var(--teal-100);
  color: #085041;
}
.ph-lab-badge--analyzed {
  background: var(--teal-100);
  color: #085041;
  border: 1px solid #9fe1cb;
}

.ph-lab-desc {
  flex: 1;
  font-size: 12px;
  color: var(--gray-500);
  /* Respeta \n en descripciones multilínea de resultados de lab (ORU^R01).
     Sin esto, "Glucemia: 95\nHemoglobina: 14.2\n..." se renderiza en una
     sola línea. Ver UC-REG-049 (selector corregido — el anterior apuntaba a
     .ph-ind-name pero la sección Lab usa esta clase). */
  white-space: pre-line;
}

.ph-lab-empty {
  font-style: italic;
  color: var(--gray-400);
}

.ph-lab-chips {
  flex: 1;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.ph-lab-chip {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  background: var(--gray-50);
  color: var(--gray-500);
  border: 0.5px solid var(--gray-100);
}

.ph-lab-chip--more {
  background: var(--navy-100);
  color: var(--navy-600);
  border-color: #b5d4f4;
}

.ph-lab-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.ph-upload-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  margin-bottom: 16px;
}

.ph-upload-btn {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #b5d4f4;
  background: #e1eaf6;
  color: #0a3d7a;
  cursor: pointer;
  transition: background 0.12s;
}

.ph-upload-btn:hover {
  background: #c8d8f0;
}
</style>
